import {
  ButtonSize,
  ButtonStyle,
  ConstrainedLayout,
  Notice,
} from '@versita/frontend/campus-ui-web';
import { setTimetableId } from '@versita/frontend/timetable-state';
import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import environment from '../../environments/environment';
import { useUserWidget } from '../../hooks/useUserWidget';
import { CodeShareWarning } from '../../molecules/CodeShareWarning';
import { TimetableView } from '../../organisms/TimetableView';
import telemetry from '../../services/telemetry';
import { useAppDispatch, useTimetableState } from '../../state';
import { courseTimetableContent as content } from '@versita/shared/text-content';
import {
  StyleDesktopTimetableHeaderButton,
  StyledCourseTimetableCourseYear,
  StyledCourseTimetableSubheading,
  StyledMobileTimetableHeaderButton,
} from './CourseTimetable.styles';
import { useCourseById } from './hooks/useCourseById';
import { useCourseTimetableEvents } from './hooks/useCourseEvents';
import * as Sentry from '@sentry/react';

export type CourseTimetablePageParams = {
  courseId: string;
  courseYear: string;
};

/**
 * Page showing the timetable for a specific course
 * (provided by the URL parameter courseId)
 */
export const CourseTimetable = () => {
  const timetableState = useTimetableState();
  const dispatch = useAppDispatch();
  const userWidgetProps = useUserWidget();
  const { courseId, courseYear } = useParams() as CourseTimetablePageParams;
  const [queryParams] = useSearchParams();
  const { course, isFetchingCourse } = useCourseById(courseId);
  const { timetableItems, isFetchingEvents } = useCourseTimetableEvents({
    courseId,
    courseYear,
    weekStart: timetableState.timetableWeekStart,
  });

  useEffect(() => {
    telemetry.trackPageView({ name: 'CourseTimetable' });
  }, []);

  useEffect(() => {
    if (courseId && courseYear) {
      Sentry.setContext('timetable', {
        type: 'Course',
        courseId,
        courseYear,
      });
      dispatch(setTimetableId(`${courseId}|${courseYear}`));
    }
  }, [courseId, courseYear, dispatch]);

  return (
    <>
      <CodeShareWarning />
      <ConstrainedLayout
        userWidget={userWidgetProps}
        heading={{
          title: (
            <>
              {course?.name || ''}{' '}
              <StyledCourseTimetableCourseYear>
                Year {courseYear}
              </StyledCourseTimetableCourseYear>
              {!queryParams.has('hccb') && (
                <StyledMobileTimetableHeaderButton
                  to="/courses"
                  $buttonStyle={ButtonStyle.Secondary}
                  $buttonSize={ButtonSize.Small}
                  $fullWidth
                >
                  Change Course
                </StyledMobileTimetableHeaderButton>
              )}
            </>
          ),
          subtitle: (
            <StyledCourseTimetableSubheading>
              Course Timetables
              {!queryParams.has('hccb') && (
                <StyleDesktopTimetableHeaderButton
                  $buttonStyle={ButtonStyle.Secondary}
                  $buttonSize={ButtonSize.Small}
                  to="/courses"
                >
                  Change Course
                </StyleDesktopTimetableHeaderButton>
              )}
            </StyledCourseTimetableSubheading>
          ),
          loading: !!isFetchingCourse,
        }}
      >
        <Notice
          title={
            timetableState.redirectedToCourseTimetable
              ? content.notice.title.redirected
              : content.notice.title.default
          }
        >
          {content.notice.body[0]}
          <a
            href={content.notice.body[1].href}
            target="_blank"
            title="Timetable Guidance Page"
            rel="nofollow noreferrer"
          >
            {content.notice.body[1].text}
          </a>
          .{' '}
          {timetableState.currentPersonalTimetableAvailable && (
            <p>
              <h4 style={{ margin: '1.6rem 0 0.8rem' }}>
                {content.notice.goback.pre}
              </h4>
              <Link to="/?nordr">{content.notice.goback.link}</Link>
              {content.notice.goback.post}
            </p>
          )}
        </Notice>
        <TimetableView
          timetableItems={timetableItems}
          loading={isFetchingEvents}
          icsUrl={`${environment.apis.studentPortalApi.endpoint}/events/timetable/course?courseId=${courseId}&courseYear=${courseYear}&format=ics`}
        />
      </ConstrainedLayout>
    </>
  );
};

export default CourseTimetable;
